import { z } from "zod";
import { isoDateTimeString, uuidSchema } from "@chotca/config";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export const interviewCreditPurchaseSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  packCode: z.string(),
  amountVnd: z.string(),
  creditCount: z.number().int(),
  unitPriceVnd: z.string(),
  status: z.enum(["PENDING", "PAID", "EXPIRED", "FAILED"]),
  provider: z.enum(["SEPAY"]),
  providerOrderId: z.string(),
  providerCheckoutUrl: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  providerMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  deadlineAt: isoDateTimeString,
  paidAt: isoDateTimeString.nullable(),
  creditedAt: isoDateTimeString.nullable(),
  createdAt: isoDateTimeString,
});

export const createCreditPurchaseRequestSchema = z.object({
  packCode: z.string().min(1),
  returnUrl: z.string().url(),
  cancelUrl: z.string().url(),
  errorUrl: z.string().url().optional(),
});

// FE submits this form to providerCheckoutUrl to redirect to the hosted page.
export const checkoutHandoffSchema = z.object({
  url: z.string().url(),
  formFields: z.record(z.string(), z.string()),
});

export const createCreditPurchaseResponseSchema = z.object({
  purchase: interviewCreditPurchaseSchema,
  checkout: checkoutHandoffSchema,
});

export const syncCreditPurchaseResponseSchema = z.object({
  purchase: interviewCreditPurchaseSchema,
  // Provider-reported status, normalised. Surfaced even when our local row
  // didn't change so the FE can decide what message to show.
  providerStatus: z.enum([
    "PENDING",
    "PAID",
    "CANCELLED",
    "EXPIRED",
    "FAILED",
    "UNKNOWN",
  ]),
  // True iff this sync transitioned the row to PAID — for the "đã cộng X lượt" toast.
  creditedNow: z.boolean(),
});

export const interviewCreditPurchaseListSchema = z.array(
  interviewCreditPurchaseSchema,
);

export const interviewCreditPurchaseStatusEnum = z.enum([
  "PENDING",
  "PAID",
  "EXPIRED",
  "FAILED",
]);

export type InterviewCreditPurchase = z.infer<
  typeof interviewCreditPurchaseSchema
>;
export type InterviewCreditPurchaseStatus = z.infer<
  typeof interviewCreditPurchaseStatusEnum
>;
export type CreateCreditPurchaseRequest = z.infer<
  typeof createCreditPurchaseRequestSchema
>;
export type CreateCreditPurchaseResponse = z.infer<
  typeof createCreditPurchaseResponseSchema
>;
export type SyncCreditPurchaseResponse = z.infer<
  typeof syncCreditPurchaseResponseSchema
>;

export type ListCreditPurchasesQuery = {
  status?: InterviewCreditPurchaseStatus;
  limit?: number;
};

export function createCreditPurchasesResource(ctx: RequestContext) {
  return {
    create(orgId: string, body: CreateCreditPurchaseRequest) {
      const payload = createCreditPurchaseRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/interview-credit-purchases`,
        body: payload,
        dataSchema: createCreditPurchaseResponseSchema,
        auth: true,
      });
    },

    list(orgId: string, query: ListCreditPurchasesQuery = {}) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/interview-credit-purchases`,
        query: {
          ...(query.status ? { status: query.status } : {}),
          ...(query.limit ? { limit: String(query.limit) } : {}),
        },
        dataSchema: interviewCreditPurchaseListSchema,
        auth: true,
      });
    },

    get(orgId: string, purchaseId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/interview-credit-purchases/${purchaseId}`,
        dataSchema: interviewCreditPurchaseSchema,
        auth: true,
      });
    },

    // Force a server-to-server status check against the provider. Idempotent.
    // Returns { creditedNow: true } if this call was the one that flipped to PAID.
    sync(orgId: string, purchaseId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/interview-credit-purchases/${purchaseId}/sync`,
        dataSchema: syncCreditPurchaseResponseSchema,
        auth: true,
      });
    },

    // User-initiated cancel of a pending purchase. 409 if already PAID.
    cancel(orgId: string, purchaseId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/interview-credit-purchases/${purchaseId}/cancel`,
        dataSchema: interviewCreditPurchaseSchema,
        auth: true,
      });
    },
  };
}
